import { useCallback, useMemo, useState, useEffect } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Grid2,
    Stack,
    Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import Switch from '../../../Shared/Components/Switch';
import ListViewTableHeaderRow from '../../../Shared/Components/ListViewTable/ListViewTableHeaderRow';
import useGetInvoices from '../../hooks/useGetInvoices';
import useLocalStorageState from '../../../utils/useLocalStorageState';
import useClearStorageOnLeave from '../../../utils/useClearStorageOnLeave';
import { COLORS } from '../../../utils/Application_Constants';
import BookingContainerCollapse from './InvoiceInitiationTable/BookingContainerCollapse';
import {
    InvoicingSelectionContextProvider,
    useInvoicingSelectionContext,
} from '../../Contexts/InvoicingSelectionContext';
import useInvoicingFilters from '../../hooks/Invoicing/useFilters';
import FilterDrawer from '../../../Shared/Components/FilterDrawer/FilterDrawer';
import SearchInputWithDropdown from '../../../Shared/Components/SearchInputWithDropdown';
import {
    generateIdForExpenseWrapper,
    initiationSearchOptions,
    wrapExpenseItems,
} from '../../Configurations/invoicingConfig';
import { pluralize } from '../../../utils/helpers';
import { formatFiltersForApi } from '../../../utils/filterUtils';
import InvoiceInitiationConfirmationModal from './InvoiceInitiationTable/InvoiceInitiationConfirmationModal';
import PaginationBar from '../../../Shared/Components/PaginationBar';
import useAdminAuthContext from '../../hooks/useAdminAuthContext';
import { useToast } from '../../Contexts/ToastContext';
import {
    ShiftSelectionContextProvider,
    useEnhancedShiftSelectionContext,
} from './ShiftSelectionContext';
import PreLoadInterceptor from './PreLoadInterceptor';

const tableHeaders = [
    { label: 'Client', key: 'client', size: 1, sortable: true },
    { label: 'Clinician', key: 'clinician', size: 1, sortable: true },
    { label: 'Bookings', key: 'booking', size: 1, sortable: true },
    { label: '# of Location', key: 'numberOfLocation', size: 1, sortable: true },
    { label: 'Frequency', key: 'frequency', size: 1, sortable: true },
    { label: 'Collector', key: 'collector', size: 1, sortable: true },
    { label: 'Types', key: 'types', size: 1, sortable: true },
];

const pageSize = 20;

// Default sort for Invoice Initiation (AE2-4009 AC4): Booking ID ascending. Initialized as the
// localStorage default so a fresh load - including after AE2-6065 clear-on-leave - shows the
// Bookings column as the active ascending sort. Mirrors Invoice Management's default-sort pattern.
const DEFAULT_SORT_FIELD = 'booking';
const DEFAULT_SORT_DIRECTION = 'asc';

const InitiationTable = ({ pageType = 'invoiceInitiation' }) => {
    const [disableSelection, setDisableSelection] = useState(true);
    const [showDisabledAlert, setShowDisabledAlert] = useState(false);
    const {
        selectStatus,
        handleSelectAllBookings,
        selectedItemsCount,
        handleSetAllBookings,
        handleToggleSelectBookingChunk,
    } = useInvoicingSelectionContext();
    const { location, replace, push } = useHistory();
    const { state: doNotSendToClient, setValue: setDoNotSendToClient } = useLocalStorageState(
        'invoiceInitiationDoNotSendToClient',
        false
    );
    // Stored separately from list state so clear-on-leave never wipes the toggle itself.
    const { state: rememberSearchAndFilters, setValue: setRememberSearchAndFilters } =
        useLocalStorageState(`${pageType}RememberSearchAndFilters`, false);
    const [successState, setSuccessState] = useState({ showAlert: false, count: 0, action: '' });
    const [innerSearchIds, setInnerSearchIds] = useState(null);
    const { showSuccessToast } = useToast();

    useClearStorageOnLeave(
        [
            `${pageType}SortField`,
            `${pageType}SortDirection`,
            `${pageType}Search`,
            `${pageType}InnerSearch`,
            `${pageType}Filters`,
        ],
        [],
        { enabled: !rememberSearchAndFilters }
    );

    const { state: sortBy, setValue: setSortBy } = useLocalStorageState(
        `${pageType}SortField`,
        DEFAULT_SORT_FIELD
    );
    const { state: sortDirection, setValue: setSortDirection } = useLocalStorageState(
        `${pageType}SortDirection`,
        DEFAULT_SORT_DIRECTION
    );
    const { state: searchData, setValue: setSearch } = useLocalStorageState(`${pageType}Search`, {
        searchWord: '',
        searchColumn: '',
    });
    const { state: innerSearchData, setValue: setInnerSearch } = useLocalStorageState(
        `${pageType}InnerSearch`,
        { innerSearchWord: '', innerSearchColumn: '' }
    );

    const { isInvoicingSystemAdminUser } = useAdminAuthContext();

    const {
        filters,
        handleApplyFilter,
        resetFilters,
        filtersSetup,
        isLoadingFilterConfig,
        appliedFiltersDescription,
        hasAppliedFilters,
    } = useInvoicingFilters(pageType);

    const [fetchEnabled, setFetchEnabled] = useState(false);
    const enableFetching = useCallback(() => {
        setFetchEnabled(true);
    }, []);

    const [page, setPage] = useState(() => {
        const urlParams = new URLSearchParams(location.search);
        if (urlParams.has('page') && parseInt(urlParams.get('page'), 10) > 0) {
            return parseInt(urlParams.get('page'), 10);
        }
        replace(`${location.pathname}?page=1`);
        return 1;
    });
    const handlePageChange = (_, newPage) => {
        push(`${location.pathname}?page=${newPage}`);
        setPage(newPage);
    };

    const requestParams = useMemo(
        () => ({
            offset: (page - 1) * pageSize,
            pageSize,
            ...formatFiltersForApi(filters, filtersSetup),
            ...(sortBy ? { orderBy: sortBy, dir: sortDirection } : {}),
            searchColumn: searchData?.searchColumn,
            ...(searchData?.searchWord ? { searchWord: searchData?.searchWord } : {}),
        }),
        [page, sortBy, sortDirection, filters, filtersSetup, searchData]
    );

    const {
        data: overallBookings,
        isLoading: overallBookingsLoading,
        isFetching: overallBookingsFetching,
        isError: overallBookingsError,
        refetch: refetchGetInvoices,
    } = useGetInvoices(requestParams, { enabled: fetchEnabled });

    // AE2-6285: select-all needs the full set of matching booking IDs (includeAllIds), which is
    // the expensive part of the proc. Fetch it in a SEPARATE, non-blocking query keyed on the
    // active filters/search (not page) so the main list paints immediately while the select-all
    // data loads in the background. allIds is independent of paging/sort server-side.
    const allIdsParams = useMemo(
        () => ({
            offset: 0,
            pageSize: 1,
            ...formatFiltersForApi(filters, filtersSetup),
            searchColumn: searchData?.searchColumn,
            ...(searchData?.searchWord ? { searchWord: searchData?.searchWord } : {}),
            includeAllIds: true,
        }),
        [filters, filtersSetup, searchData]
    );

    const {
        isFetching: allIdsFetching,
        isError: allIdsError,
        refetch: refetchAllIds,
    } = useGetInvoices(allIdsParams, {
        enabled: fetchEnabled && isInvoicingSystemAdminUser && !innerSearchData?.innerSearchWord,
        onSuccess: (response) => {
            if (!response?.allIds) return;
            const selectableBookings = response.allIds.map((booking) => ({
                bookingId: booking.bookingId,
                clientId: booking.clientId,
                timesheets: booking.processedTimesheets || [],
                expenses:
                    booking.expenses?.length > 0
                        ? [
                              {
                                  id: generateIdForExpenseWrapper(booking.expenses),
                                  items: booking.expenses,
                              },
                          ]
                        : [],
            }));
            handleSetAllBookings(selectableBookings);
        },
    });

    const {
        data: innerSearchBookings,
        isLoading: innerSearchLoading,
        isFetching: innerSearchFetching,
        isError: innerSearchError,
        refetch: refetchInnerSearchGetInvoices,
    } = useGetInvoices(
        {
            ...requestParams,
            ...(innerSearchData?.innerSearchWord ? innerSearchData : {}),
            // AE2-6285: inner search still needs allIds to compute the matched-ID set. Preserve the
            // original page===1 admin gating (requestParams no longer carries includeAllIds).
            ...(isInvoicingSystemAdminUser && page === 1 ? { includeAllIds: true } : {}),
        },
        {
            enabled: fetchEnabled && !!innerSearchData?.innerSearchWord,
            onSuccess: (response) => {
                if (!response?.allIds) return;
                setInnerSearchIds(response.allIds.map((booking) => booking.bookingId));
            },
        }
    );

    const bookings = innerSearchData?.innerSearchWord ? innerSearchBookings : overallBookings;
    const isLoading = innerSearchData?.innerSearchWord
        ? innerSearchLoading
        : overallBookingsLoading;
    const isFetching = innerSearchData?.innerSearchWord
        ? innerSearchFetching
        : overallBookingsFetching;
    const isError = innerSearchData?.innerSearchWord ? innerSearchError : overallBookingsError;

    const handleRefetch = () => {
        if (page !== 1) {
            handlePageChange(null, 1);
        } else {
            refetchGetInvoices();
            if (innerSearchData?.innerSearchWord) refetchInnerSearchGetInvoices();
        }
        // AE2-6285: the select-all ID set lives in its own query now; refresh it after actions
        // (invoiced items drop off the list) for the non-inner-search path.
        if (!innerSearchData?.innerSearchWord) refetchAllIds();
    };

    useEffect(() => {
        if (page !== 1) handlePageChange(null, 1);
    }, [filters, searchData, innerSearchData]);

    useEffect(() => {
        // AE2-6285: for the non-inner-search path the selectable-ID set now loads in a separate
        // query, so selection must stay disabled until that set is ready (the list itself renders
        // independently). Inner-search selection stays gated by isFetching/isError as before.
        const selectableFetching = !innerSearchData?.innerSearchWord && allIdsFetching;
        const selectableError = !innerSearchData?.innerSearchWord && allIdsError;
        setDisableSelection(
            !isInvoicingSystemAdminUser ||
                isFetching ||
                isError ||
                selectableFetching ||
                selectableError
        );
        setShowDisabledAlert(isError || selectableError);
    }, [
        isInvoicingSystemAdminUser,
        isFetching,
        isError,
        allIdsFetching,
        allIdsError,
        innerSearchData?.innerSearchWord,
    ]);

    useEffect(() => {
        if (successState.showAlert) {
            showSuccessToast('Your invoice(s) have been queued for creation.');
            setSuccessState({ showAlert: false, count: 0, action: '' });
        }
    }, [successState]);

    const [showInvoiceInitiationConfirmationModal, setShowInvoiceInitiationConfirmationModal] =
        useState(false);
    const [invoiceAction, setInvoiceAction] = useState('invoice'); // 'invoice' or 'pre-invoice'
    const totalCount = bookings?.totalCount || 0;

    const handlePreInvoiceClick = () => {
        setInvoiceAction('pre-invoice');
        setShowInvoiceInitiationConfirmationModal(true);
    };

    const handleInvoiceClick = () => {
        setInvoiceAction('invoice');
        setShowInvoiceInitiationConfirmationModal(true);
    };

    const handleSortChange = useCallback((key, direction) => {
        setSortBy(key);
        setSortDirection(direction);
    }, []);

    const renderPagination = (position) => {
        if (isLoading || !totalCount || !fetchEnabled) return null;
        return (
            <Box
                sx={{ mt: position === 'top' ? 0 : '15px', mb: position === 'bottom' ? 0 : '15px' }}
            >
                <PaginationBar
                    page={page}
                    totalPages={Math.ceil(totalCount / pageSize)}
                    handlePageChange={handlePageChange}
                />
            </Box>
        );
    };

    const bookingItems = useMemo(() => bookings?.data?.map(wrapExpenseItems) || [], [bookings]);

    const { handleSelectBookingClick, resetAnchor } = useEnhancedShiftSelectionContext({
        disableSelection,
        bookings: bookingItems,
    });

    useEffect(() => {
        // Reset the shift-range anchor whenever the visible list changes (paging/sorting/filtering triggers new `bookings`)
        resetAnchor();
    }, [bookings]);

    const handleSelectAll = useCallback(() => {
        if (isFetching || isError) return;
        resetAnchor();
        if (innerSearchData?.innerSearchWord && innerSearchIds?.length > 0) {
            handleToggleSelectBookingChunk(innerSearchIds);
        } else {
            handleSelectAllBookings();
        }
    }, [isFetching, isError, innerSearchData?.innerSearchWord, innerSearchIds]);

    const handleClearFiltersAndSearchCriteria = () => {
        resetFilters();
        setSearch((prevSearch) => ({ ...prevSearch, searchWord: '' }));
        setInnerSearch((prevInnerSearch) => ({ ...prevInnerSearch, innerSearchWord: '' }));
        enableFetching();
    };

    return (
        <>
            <Grid2 container alignItems="center" spacing={1} mt="12px">
                <FilterDrawer
                    isLoading={isLoadingFilterConfig || isFetching}
                    config={filtersSetup}
                    onChange={(appliedFilters) => {
                        handleApplyFilter(appliedFilters);
                        enableFetching();
                    }}
                    storedValues={filters}
                />
                <FormControlLabel
                    sx={{
                        ml: 1,
                        '& .MuiFormControlLabel-label': {
                            color: COLORS.LT_MIDNIGHT,
                            fontSize: 14,
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '16.5px',
                        },
                    }}
                    checked={!!rememberSearchAndFilters}
                    onChange={(e) => setRememberSearchAndFilters(e.target.checked)}
                    control={<Switch />}
                    label="Remember my search and filters"
                />
                <SearchInputWithDropdown
                    isLoading={isFetching}
                    options={initiationSearchOptions}
                    initialSearch={searchData?.searchWord}
                    initialSearchKey={searchData?.searchColumn}
                    onSubmit={({ search, searchKey }) => {
                        setSearch({ searchWord: search, searchColumn: searchKey });
                        enableFetching();
                    }}
                    sx={{ ml: 'auto' }}
                />
            </Grid2>

            <Typography sx={{ my: 2, fontSize: 12, fontWeight: '400', color: COLORS.LT_MIDNIGHT }}>
                {fetchEnabled && `${totalCount} ${pluralize(totalCount, 'item')}`}
                {appliedFiltersDescription}
            </Typography>

            {renderPagination('top')}

            {showDisabledAlert && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShowDisabledAlert(false)}>
                    Selection has been disabled due to an error fetching selectable items. Please
                    refresh the page or adjust your filters.
                </Alert>
            )}

            <Box
                sx={{
                    py: '22px',
                    px: '26px',
                    backgroundColor: COLORS.WHITE,
                    borderRadius: '10px',
                }}
            >
                <Typography>Unbill items</Typography>

                {!fetchEnabled ? (
                    <PreLoadInterceptor
                        hasPreExistentFilters={
                            hasAppliedFilters ||
                            !!searchData?.searchWord ||
                            !!innerSearchData?.innerSearchWord
                        }
                        onConfirm={enableFetching}
                        onCancel={handleClearFiltersAndSearchCriteria}
                    />
                ) : (
                    <>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mt: '30px', mb: '20px' }}
                        >
                            <SearchInputWithDropdown
                                isLoading={isFetching}
                                options={initiationSearchOptions}
                                placeholder="Search within results"
                                initialSearch={innerSearchData?.innerSearchWord}
                                initialSearchKey={innerSearchData?.innerSearchColumn}
                                onSubmit={({ search, searchKey }) =>
                                    setInnerSearch({
                                        innerSearchWord: search,
                                        innerSearchColumn: searchKey,
                                    })
                                }
                            />
                            {isInvoicingSystemAdminUser && (
                                <>
                                    <FormControlLabel
                                        sx={{
                                            ml: 'auto',
                                            '& .MuiFormControlLabel-label': {
                                                color: COLORS.LT_MIDNIGHT,
                                                fontSize: 14,
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                lineHeight: '16.5px',
                                            },
                                        }}
                                        checked={doNotSendToClient}
                                        onChange={(e) => setDoNotSendToClient(e.target.checked)}
                                        control={<Switch />}
                                        label="Do not send to client"
                                    />
                                    <Stack direction="row" alignItems="center" gap="10px">
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            disabled={selectedItemsCount === 0}
                                            onClick={handlePreInvoiceClick}
                                        >
                                            Pre-invoice ({selectedItemsCount})
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            disabled={selectedItemsCount === 0}
                                            onClick={handleInvoiceClick}
                                        >
                                            Invoice Selected ({selectedItemsCount})
                                        </Button>
                                    </Stack>
                                </>
                            )}
                        </Stack>

                        {bookingItems.length > 0 ? (
                            <>
                                <ListViewTableHeaderRow
                                    selectStatus={
                                        // eslint-disable-next-line no-nested-ternary
                                        isFetching
                                            ? 'loading'
                                            : !disableSelection
                                            ? selectStatus
                                            : undefined
                                    }
                                    onSelectAll={!disableSelection ? handleSelectAll : undefined}
                                    selectColumnProps={{
                                        sx: {
                                            justifyContent: 'flex-end',
                                            width: '90px',
                                            pr: '2.5px !important',
                                        },
                                    }}
                                    columns={tableHeaders}
                                    onSortChange={handleSortChange}
                                    sortBy={sortBy}
                                    sortDirection={sortDirection}
                                    shouldCleanSort
                                    sortIconPosition="left"
                                />

                                {bookingItems.map((booking) => (
                                    <BookingContainerCollapse
                                        key={booking.bookingId}
                                        booking={booking}
                                        disableSelection={disableSelection}
                                        handleSelectBooking={handleSelectBookingClick}
                                    />
                                ))}
                            </>
                        ) : (
                            <div>
                                {isLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    'No results found'
                                )}
                            </div>
                        )}
                    </>
                )}
            </Box>
            {showInvoiceInitiationConfirmationModal && (
                <InvoiceInitiationConfirmationModal
                    open
                    onClose={() => setShowInvoiceInitiationConfirmationModal(false)}
                    action={invoiceAction}
                    doNotSendToClient={invoiceAction === 'pre-invoice' ? true : doNotSendToClient}
                    setDoNotSendToClient={setDoNotSendToClient}
                    setSuccessState={setSuccessState}
                    refetch={handleRefetch}
                    invoiceThroughDate={filters?.invoiceThroughDate}
                />
            )}

            {renderPagination('bottom')}
        </>
    );
};

export default function InitiationTableWithSelectionContext() {
    return (
        <InvoicingSelectionContextProvider bookings={[]} totalCount={[]}>
            <ShiftSelectionContextProvider>
                <InitiationTable />
            </ShiftSelectionContextProvider>
        </InvoicingSelectionContextProvider>
    );
}
